import type {
  CodePreviewTarget,
  PreviewViewportMode,
  ProjectDetail,
  ProjectPanelMode,
  ProjectSettings,
  ProjectSummary,
  ProjectTaskItem,
  SandboxStatus,
  VersionContent,
  VersionHistoryRecord,
} from './types';
import type { VersionTimelineItem } from '@/services/project';
import { create } from 'zustand';
import { hasProjectSummaryData } from '@/helpers/projectSummary';
import { ProjectStatus, Stage } from './types';

function dedupeVersionHistoryRecords(records: VersionHistoryRecord[]): VersionHistoryRecord[] {
  const map = new Map<string, VersionHistoryRecord>();

  records.forEach(record => {
    if (!record.versionId || !record.previewUrl) {
      return;
    }

    map.set(record.versionId, record);
  });

  return Array.from(map.values()).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

function mergeVersionTimelineItems(
  currentItems: VersionTimelineItem[],
  nextItems: VersionTimelineItem[],
): VersionTimelineItem[] {
  const seen = new Set<string>();

  return [...currentItems, ...nextItems].filter(item => {
    const key = `${item.type}:${item.id}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

interface ProjectState {
  stage: Stage;
  useExisting: boolean;
  projectName: string;
  projectId: string;
  sessionId: string;
  sandboxId: string;
  sandboxStatus: SandboxStatus;
  userId: string;
  projectList: ProjectDetail[];
  projectListLoading: boolean;
  tasks: ProjectTaskItem[];
  projectSummary: ProjectSummary | null;
  hasProjectSummaryData: boolean;
  status: ProjectStatus;
  errorMessage: string;
  currentProjectDetail: ProjectDetail | null;
  currentProjectSettings: ProjectSettings | null;
  /**
   * 标识项目是否为刚创建的新项目
   * 用于跳过重复的沙盒启动操作
   * 该标识在启动流程完成后会自动清除
   */
  isNewlyCreated: boolean;
  projectPanelMode: ProjectPanelMode;
  previewViewportMode: PreviewViewportMode;

  versions: Map<string, VersionContent>;
  versionTimelineItems: VersionTimelineItem[];
  versionHistoryRecords: VersionHistoryRecord[];
  versionHistoryNextCursor: string | null;
  versionHistoryHasNextPage: boolean;
  versionHistoryLoading: boolean;
  versionHistoryInitialized: boolean;
  versionHistoryError: boolean;
  versionHistoryAppendError: boolean;
  versionTimelineGeneration: number;
  previewVersion: VersionContent | null;
  snapshotRouteOverride: string | null;
  codePreviewTarget: CodePreviewTarget | null;
  lastVersionId: string;
  switchingVersion: VersionContent | null;
  isRollbackBlockedByChatActivity: boolean;

  setStage: (stage: Stage) => void;
  setUseExisting: (useExisting: boolean) => void;
  setProjectName: (projectName: string) => void;
  setProjectId: (projectId: string) => void;
  setSessionId: (sessionId: string) => void;
  setSandboxId: (sandboxId: string) => void;
  setSandboxStatus: (sandboxStatus: SandboxStatus) => void;
  setUserId: (userId: string) => void;
  setProjectList: (projectList: ProjectDetail[]) => void;
  setProjectListLoading: (loading: boolean) => void;
  setTasks: (tasks: ProjectTaskItem[]) => void;
  setProjectSummary: (projectSummary: ProjectSummary | null) => void;
  setStatus: (status: ProjectStatus) => void;
  setErrorMessage: (errorMessage: string) => void;
  setCurrentProjectDetail: (detail: ProjectDetail | null) => void;
  setCurrentProjectSettings: (settings: ProjectSettings | null) => void;
  updateCurrentProjectSettings: (updates: Partial<ProjectSettings>) => void;
  setIsNewlyCreated: (isNewlyCreated: boolean) => void;
  addVersion: (messageId: string, content: VersionContent) => void;
  getVersion: (messageId: string) => VersionContent | null;
  replaceVersionTimelineItems: (items: VersionTimelineItem[]) => void;
  appendVersionTimelineItems: (items: VersionTimelineItem[]) => void;
  prependVersionTimelineItem: (item: VersionTimelineItem) => void;
  setVersionFavorite: (commitId: string, isFavorite: boolean) => void;
  replaceVersionHistoryRecords: (records: VersionHistoryRecord[]) => void;
  appendVersionHistoryRecords: (records: VersionHistoryRecord[]) => void;
  prependVersionHistoryRecord: (record: VersionHistoryRecord) => void;
  setVersionHistoryCursorState: (nextCursor: string | null, hasNextPage: boolean) => void;
  setVersionHistoryLoading: (loading: boolean) => void;
  setVersionHistoryInitialized: (initialized: boolean) => void;
  setVersionHistoryError: (error: boolean) => void;
  setVersionHistoryAppendError: (error: boolean) => void;
  invalidateVersionTimeline: () => void;
  resetVersionHistory: () => void;
  setPreviewVersion: (versionContent: VersionContent | null) => void;
  setSnapshotRouteOverride: (route: string | null) => void;
  setCodePreviewTarget: (target: CodePreviewTarget | null) => void;
  setSwitchingVersion: (switchingVersion: VersionContent | null) => void;
  getIsSwitchingVersion: () => boolean;
  setIsRollbackBlockedByChatActivity: (blocked: boolean) => void;
  getIsRollbackBlocked: () => boolean;
  resetVersionState: () => void;
  setLastVersionId: (versionId: string) => void;
  // 当前是否只有一个版本
  getIsOneVersion: () => boolean;
  setProjectPanelMode: (mode: ProjectPanelMode) => void;
  setPreviewViewportMode: (mode: PreviewViewportMode) => void;
  reset: () => void;
}

function getInitialState() {
  return {
    stage: Stage.Input,
    useExisting: true,
    projectName: '',
    projectId: '',
    sessionId: '',
    sandboxId: '',
    sandboxStatus: 'unknown' as SandboxStatus,
    userId: '',
    projectList: [],
    projectListLoading: false,
    tasks: [],
    projectSummary: null,
    hasProjectSummaryData: false,
    status: ProjectStatus.PENDING,
    errorMessage: '',
    currentProjectDetail: null,
    currentProjectSettings: null,
    versions: new Map<string, VersionContent>(),
    versionTimelineItems: [],
    versionHistoryRecords: [],
    versionHistoryNextCursor: null,
    versionHistoryHasNextPage: true,
    versionHistoryLoading: false,
    versionHistoryInitialized: false,
    versionHistoryError: false,
    versionHistoryAppendError: false,
    versionTimelineGeneration: 0,
    isNewlyCreated: false,
    previewVersion: null,
    snapshotRouteOverride: null,
    codePreviewTarget: null,
    switchingVersion: null,
    isRollbackBlockedByChatActivity: false,
    lastVersionId: '',
    projectPanelMode: 'preview' as ProjectPanelMode,
    previewViewportMode: 'desktop' as PreviewViewportMode,
  };
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  ...getInitialState(),

  setStage: stage => set({ stage }),
  setUseExisting: useExisting => set({ useExisting }),
  setProjectName: projectName => set({ projectName }),
  setProjectId: projectId =>
    set(state => {
      const isProjectChanged = state.projectId !== projectId;
      const shouldClearProjectSummary = !projectId || isProjectChanged;

      return {
        projectId,
        sandboxId: isProjectChanged ? '' : state.sandboxId,
        sandboxStatus: isProjectChanged ? 'unknown' : state.sandboxStatus,
        projectSummary: shouldClearProjectSummary ? null : state.projectSummary,
        hasProjectSummaryData: shouldClearProjectSummary ? false : state.hasProjectSummaryData,
        versions: isProjectChanged ? new Map<string, VersionContent>() : state.versions,
        versionTimelineItems: isProjectChanged ? [] : state.versionTimelineItems,
        versionHistoryRecords: isProjectChanged ? [] : state.versionHistoryRecords,
        versionHistoryNextCursor: isProjectChanged ? null : state.versionHistoryNextCursor,
        versionHistoryHasNextPage: isProjectChanged ? true : state.versionHistoryHasNextPage,
        versionHistoryLoading: isProjectChanged ? false : state.versionHistoryLoading,
        versionHistoryInitialized: isProjectChanged ? false : state.versionHistoryInitialized,
        versionHistoryError: isProjectChanged ? false : state.versionHistoryError,
        versionHistoryAppendError: isProjectChanged ? false : state.versionHistoryAppendError,
        versionTimelineGeneration: isProjectChanged
          ? state.versionTimelineGeneration + 1
          : state.versionTimelineGeneration,
        previewVersion: isProjectChanged ? null : state.previewVersion,
        snapshotRouteOverride: isProjectChanged ? null : state.snapshotRouteOverride,
        codePreviewTarget: isProjectChanged ? null : state.codePreviewTarget,
        switchingVersion: isProjectChanged ? null : state.switchingVersion,
        isRollbackBlockedByChatActivity: isProjectChanged ? false : state.isRollbackBlockedByChatActivity,
        lastVersionId: isProjectChanged ? '' : state.lastVersionId,
      };
    }),
  setSessionId: sessionId => set({ sessionId }),
  setSandboxId: sandboxId =>
    set({
      sandboxId,
      sandboxStatus: sandboxId ? 'available' : 'unknown',
    }),
  setSandboxStatus: sandboxStatus => set({ sandboxStatus }),
  setUserId: userId => set({ userId }),
  setProjectList: projectList => set({ projectList }),
  setProjectListLoading: loading => set({ projectListLoading: loading }),
  setTasks: tasks => set({ tasks }),
  setProjectSummary: projectSummary =>
    set({
      projectSummary,
      hasProjectSummaryData: hasProjectSummaryData(projectSummary),
    }),
  setStatus: status => set({ status }),
  setErrorMessage: errorMessage => set({ errorMessage }),
  setCurrentProjectDetail: (detail: ProjectDetail | null) =>
    set((state) => {
      const shouldPreserveGeneratingStatus =
        detail !== null
        && state.currentProjectDetail?.id === detail.id
        && state.status === ProjectStatus.GENERATING
        && (detail.status === ProjectStatus.CHAT || detail.status === ProjectStatus.PENDING);
      const currentProjectDetail = shouldPreserveGeneratingStatus
        ? { ...detail, status: ProjectStatus.GENERATING }
        : detail;

      return {
        currentProjectDetail,
        status: currentProjectDetail?.status ?? state.status,
        projectSummary: detail ? state.projectSummary : null,
        hasProjectSummaryData: detail ? state.hasProjectSummaryData : false,
      };
    }),
  updateCurrentProjectSettings: updates =>
    set(state => ({
      currentProjectSettings: state.currentProjectSettings ? { ...state.currentProjectSettings, ...updates } : null,
    })),
  setCurrentProjectSettings: settings => set({ currentProjectSettings: settings }),
  setIsNewlyCreated: isNewlyCreated => set({ isNewlyCreated }),
  addVersion: (messageId, content) => {
    if (messageId) {
      set(state => ({
        versions: new Map(state.versions).set(messageId, content),
      }));
    };
  },
  getVersion: messageId => {
    const version = get().versions.get(messageId);
    return version || null;
  },
  replaceVersionTimelineItems: versionTimelineItems => set({
    versionTimelineItems: mergeVersionTimelineItems([], versionTimelineItems),
  }),
  appendVersionTimelineItems: items => {
    if (!items.length) {
      return;
    }

    set(state => ({
      versionTimelineItems: mergeVersionTimelineItems(state.versionTimelineItems, items),
    }));
  },
  prependVersionTimelineItem: item => set(state => ({
    versionTimelineItems: mergeVersionTimelineItems([item], state.versionTimelineItems),
  })),
  setVersionFavorite: (commitId, isFavorite) => set(state => ({
    versionTimelineItems: state.versionTimelineItems.map(item => (
      item.type === 'commit' && item.id === commitId
        ? { ...item, isFavorite: isFavorite ? 1 : 0 }
        : item
    )),
  })),
  replaceVersionHistoryRecords: records => set({
    versionHistoryRecords: dedupeVersionHistoryRecords(records),
  }),
  appendVersionHistoryRecords: records => {
    if (!records.length) {
      return;
    }

    set(state => ({
      versionHistoryRecords: dedupeVersionHistoryRecords([...state.versionHistoryRecords, ...records]),
    }));
  },
  prependVersionHistoryRecord: record => set(state => ({
    versionHistoryRecords: dedupeVersionHistoryRecords([record, ...state.versionHistoryRecords]),
  })),
  setVersionHistoryCursorState: (versionHistoryNextCursor, versionHistoryHasNextPage) =>
    set({
      versionHistoryNextCursor,
      versionHistoryHasNextPage,
      versionHistoryInitialized: true,
    }),
  setVersionHistoryLoading: versionHistoryLoading => set({ versionHistoryLoading }),
  setVersionHistoryInitialized: versionHistoryInitialized => set({ versionHistoryInitialized }),
  setVersionHistoryError: versionHistoryError => set({ versionHistoryError }),
  setVersionHistoryAppendError: versionHistoryAppendError => set({ versionHistoryAppendError }),
  invalidateVersionTimeline: () => set(state => ({
    versionTimelineGeneration: state.versionTimelineGeneration + 1,
  })),
  resetVersionHistory: () =>
    set(state => ({
      versionHistoryRecords: [],
      versionTimelineItems: [],
      versionHistoryNextCursor: null,
      versionHistoryHasNextPage: true,
      versionHistoryLoading: false,
      versionHistoryInitialized: false,
      versionHistoryError: false,
      versionHistoryAppendError: false,
      versionTimelineGeneration: state.versionTimelineGeneration + 1,
    })),
  setPreviewVersion: versionContent => set({ previewVersion: versionContent, snapshotRouteOverride: null }),
  setSnapshotRouteOverride: snapshotRouteOverride => set({ snapshotRouteOverride }),
  setCodePreviewTarget: codePreviewTarget => set({ codePreviewTarget }),
  setSwitchingVersion: switchingVersion => set({ switchingVersion }),
  getIsSwitchingVersion: () => Boolean(get().switchingVersion),
  setIsRollbackBlockedByChatActivity: isRollbackBlockedByChatActivity => set({ isRollbackBlockedByChatActivity }),
  getIsRollbackBlocked: () => Boolean(
    get().switchingVersion || get().isRollbackBlockedByChatActivity,
  ),
  resetVersionState: () => set(state => ({
    versions: new Map<string, VersionContent>(),
    versionTimelineItems: [],
    versionHistoryRecords: [],
    versionHistoryNextCursor: null,
    versionHistoryHasNextPage: true,
    versionHistoryLoading: false,
    versionHistoryInitialized: false,
    versionHistoryError: false,
    versionHistoryAppendError: false,
    versionTimelineGeneration: state.versionTimelineGeneration + 1,
    previewVersion: null,
    snapshotRouteOverride: null,
    codePreviewTarget: null,
    lastVersionId: '',
  })),
  setLastVersionId: versionId => set({ lastVersionId: versionId }),
  getIsOneVersion: () => get().versions.size === 1,
  setProjectPanelMode: projectPanelMode => set({ projectPanelMode }),
  setPreviewViewportMode: previewViewportMode => set({ previewViewportMode }),
  reset: () =>
    set(() => (getInitialState())),
}));

export * from './types';
